import { ref, type Ref } from 'vue'
import useDelete from '../../../core/composables/api/useDelete'
import usePut from '../../../core/composables/api/usePut'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../../../core/events/event-key'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import type { ManageableOrganization } from './useOrganization'
import { useSnackStore } from '../../../stores/snack.store'
import { useI18n } from 'vue-i18n'
import { useDialog } from '../../../core/components/base/dialog/use-dialog'
import type { Role } from '../../organization/models/organization-role'
import usePost from '../../../core/composables/api/usePost'

export const useOrganizationSettings = (organization: Ref<ManageableOrganization>) => {
  const snackStore = useSnackStore()
  const { t } = useI18n()

  const { remove: removeMember, status: deletionStatus } = useDelete<string, { memberId: string }>(
    'organization/member',
  )

  const { mutate, status: mutationStatus } = usePut<string, { organizationName: string }>(
    'organization/name',
  )

  const {
    mutate: addMemberMutate,
    status: addMemberStatus,
    data: addMemberId,
  } = usePost<string, AddMemberBody>('organization/member')

  const {
    mutate: addLoginLessMembersMutate,
    status: addLoginLessMembersStatus,
    data: loginLessMemberIds,
  } = usePost<string[], AddLoginLessMembersBody>('organization/login-less-member')

  const loginLessMemberEndpoint = ref('organization/login-less-member')
  const { mutate: updateLoginLessMemberMutate, status: updateLoginLessMemberStatus } = usePut<
    string,
    LoginLessMemberName
  >(loginLessMemberEndpoint)

  const deleteMemberBus = useEventBus(EventKey.organizationMemberDeleted)
  const createMemberBus = useEventBus(EventKey.organizationMemberCreated)
  const memberUpdatedBus = useEventBus(EventKey.organizationMemberUpdated)
  const updatedMemberPermissionsBus = useEventBus(EventKey.updatedMemberPermissions)

  const deleteMember = async (memberId: string) => {
    await removeMember({ memberId })
    if (deletionStatus.value == ApiRequestStatus.Success) {
      deleteMemberBus.emit({ memberId: memberId })
      organization.value.members = organization.value.members.filter(
        (member) => member.id != memberId,
      )
      snackStore.showInfo(t('organization.settings.deleteMemberSuccess'))
    } else {
      snackStore.showError(t('organization.settings.deleteMemberError'))
    }
  }

  const updateOrganizationName = async () => {
    await mutate({ organizationName: organization.value.name })
    if (mutationStatus.value == ApiRequestStatus.Success) {
      isOrganizionNameEditable.value = false
      snackStore.showInfo(t('organization.name.update.success'))
    } else {
      snackStore.showError(t('organization.name.update.error'))
    }
  }

  const isOrganizionNameEditable = ref(false)

  const editOrganizationName = () => {
    isOrganizionNameEditable.value = true
  }

  const cancelOrganizationName = () => {
    isOrganizionNameEditable.value = false
  }

  const { mutate: mutateMember, status: mutationMemberStatus } = usePut<
    string,
    { memberId: string; roles: Role[] }
  >('organization/member')

  const editedRoles = async (memberId: string, roles: Role[]) => {
    await mutateMember({ memberId, roles })
    if (mutationMemberStatus.value == ApiRequestStatus.Success) {
      organization.value.members = organization.value.members.map((member) => {
        if (member.id == memberId) {
          return { ...member, roles: roles }
        }
        return member
      })
      snackStore.showInfo(t('organization.roles.update.success'))
      updatedMemberPermissionsBus.emit({ memberId: memberId })
    } else {
      snackStore.showError(t('organization.roles.update.error'))
    }
  }

  const addMember = async (member: AddMemberBody) => {
    await addMemberMutate(member)
    if (addMemberStatus.value == ApiRequestStatus.Success) {
      snackStore.showInfo(t('organization.inviteMember.success'))
      createMemberBus.emit({ memberId: addMemberId.value })
      closeAddMemberDialog()
    } else {
      snackStore.showError(t('organization.inviteMember.error'))
    }
  }

  const addLoginLessMembers = async (body: AddLoginLessMembersBody) => {
    await addLoginLessMembersMutate(body)
    if (addLoginLessMembersStatus.value == ApiRequestStatus.Success) {
      snackStore.showInfo(t('organization.loginLessMember.success'))
      const memberId = loginLessMemberIds.value?.[0]
      if (memberId) {
        createMemberBus.emit({ memberId })
      }
      closeAddLoginLessMemberDialog()
    } else {
      snackStore.showError(t('organization.loginLessMember.error'))
    }
  }

  const updateLoginLessMember = async (memberId: string, name: LoginLessMemberName) => {
    loginLessMemberEndpoint.value = `organization/login-less-member/${memberId}`
    await updateLoginLessMemberMutate(name)
    if (updateLoginLessMemberStatus.value == ApiRequestStatus.Success) {
      organization.value.members = organization.value.members.map((member) => {
        if (member.id !== memberId || member.pending) {
          return member
        }
        return { ...member, firstName: name.firstName, lastName: name.lastName }
      })
      snackStore.showInfo(t('organization.loginLessMember.editSuccess'))
      memberUpdatedBus.emit({ memberId })
    } else {
      snackStore.showError(t('organization.loginLessMember.editError'))
    }
  }

  const {
    isOpen: isAddMemberDialogOpen,
    openDialog: openAddMemberDialog,
    closeDialog: closeAddMemberDialog,
  } = useDialog()

  const {
    isOpen: isAddLoginLessMemberDialogOpen,
    openDialog: openAddLoginLessMemberDialog,
    closeDialog: closeAddLoginLessMemberDialog,
  } = useDialog()

  return {
    deleteMember,
    deletionStatus,
    organization,
    updateOrganizationName,
    mutationStatus,
    editOrganizationName,
    isOrganizionNameEditable,
    cancelOrganizationName,
    editedRoles,
    addMember,
    addLoginLessMembers,
    updateLoginLessMember,
    isAddMemberDialogOpen,
    openAddMemberDialog,
    closeAddMemberDialog,
    isAddLoginLessMemberDialogOpen,
    openAddLoginLessMemberDialog,
    closeAddLoginLessMemberDialog,
  }
}

export interface AddMemberBody {
  emails: string[]
  sendEmail: boolean
  roles: Role[]
  teamId?: string
}

export interface LoginLessMemberName {
  firstName: string
  lastName?: string
}

export interface AddLoginLessMembersBody {
  members: LoginLessMemberName[]
  teamId?: string
}
